import { domainOf, safeExternalUrl } from '../../lib/url';
import { readGenerationPrefill, type GenerationPrefill } from './generationPrefill';

/**
 * What another app shared into the installed Autopostmate, as a brief prefill.
 *
 * The manifest's `share_target` (vite.config.ts) sends the share to `/share`
 * as `title`, `text` and `url` query parameters. Apps fill them unevenly: a
 * browser sends the page as `url`, while many Android apps leave `url` empty
 * and put "Headline https://…" in `text`. So the link is the first http(s)
 * address in `url`, then in `text`; the words are the title and whatever text
 * is left once the link is taken out.
 *
 * The result goes through `readGenerationPrefill`, the same guard every other
 * hand-off passes: a link becomes an article prefill (the research node reads
 * the page), words without a link become a topic, and nothing usable is null.
 * Nothing is stored and nothing spends; the brief is where the quota goes.
 */

const URL_PATTERN = /https?:\/\/[^\s<>"']+/i;

/**
 * The first http(s) link in some text: the span to cut out of the words (with
 * any sentence punctuation after it), and the link as the guard reads it.
 */
function firstUrl(value: string): { span: string; safe: string } | null {
  const match = URL_PATTERN.exec(value);
  if (!match) return null;
  const raw = match[0].replace(/[).,;:!?]+$/, '');
  const safe = safeExternalUrl(raw);
  return safe ? { span: match[0], safe } : null;
}

export function sharePrefill(params: URLSearchParams): GenerationPrefill | null {
  const title = (params.get('title') ?? '').trim();
  const text = (params.get('text') ?? '').trim();
  const fromText = firstUrl(text);
  const url = safeExternalUrl((params.get('url') ?? '').trim()) ?? fromText?.safe ?? null;

  // The shared text without the link in it, so the topic is words, not a URL.
  const words = (fromText ? text.replace(fromText.span, ' ') : text).replace(/\s+/g, ' ').trim();
  const heading = title || words;
  const topic = [heading, words && words !== heading ? words : ''].filter(Boolean).join('\n\n');

  if (url) {
    const source = domainOf(url) ?? 'Shared link';
    return readGenerationPrefill({
      source_url: url,
      topic: topic || source,
      title: heading || source,
      source,
    });
  }
  return readGenerationPrefill({ topic });
}
